/* eslint-disable react/jsx-props-no-spreading */
import { getCSRFHeaders, postJSON } from '@folklore/fetch';
import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { isString } from 'lodash';
import PropTypes from 'prop-types';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'wouter';

import { useGame } from '../../contexts/GameContext';
import { useDisplay } from '../../contexts/LiveDisplayContext';
import { useThemeClassName, useTheme } from '../../contexts/ThemeContext';
import StyledButton from '../buttons/StyledButton';
import Home from '../icons/Home';
import Logo from '../icons/Logo';
import StepPreview from './StepPreview';
import Footer from './footers/Footer';
import Sidebar from './sidebars/Sidebar';
import Step from './steps/Step';

import styles from '../../../styles/displays/live-quiz-controller.module.scss';

import Book from '../../../img/defis/book.png';

const propTypes = {
    vertical: PropTypes.bool.isRequired,
};

function LiveQuizController({ vertical }) {
    const display = useDisplay();

    const { name: theme } = useTheme();
    const themeClassName = useThemeClassName(styles);
    const route = useUrlGenerator();
    const { header } = useTheme();
    const [, navigate] = useLocation();

    const { logo } = header || {};
    const { component: logoComponent } = logo || {};

    const {
        slug = null,
        pageStep = null,
        pageSteps: steps = null,
        leaderboard = null,
    } = display || {};

    const { component = null } = pageStep || {};

    const game = useGame();
    const { id: pageGameId = null, step: stepId = null } = game || {};
    const step = (steps || []).find(({ id }) => id === stepId) || null;

    // console.log('pageGame', pageGame);
    // console.log('game', game);
    // console.log('displayGame', displayGame);

    const resetCurrentGame = useCallback(() => {
        postJSON(route('display.game.reset', { display: slug }), null, {
            credentials: 'include',
            headers: getCSRFHeaders(),
        });
    }, [slug, route]);

    const updateCurrentGame = useCallback(
        (newGame) => {
            // console.log('humb', newGame);
            if (newGame === null) {
                return;
            }
            const { id: finalGameId = null } = isString(newGame) ? { id: newGame } : newGame;
            postJSON(route('display.game.current', { display: slug, game: finalGameId }), null, {
                credentials: 'include',
                headers: getCSRFHeaders(),
            });
        },
        [slug, route],
    );

    const updateStep = useCallback(
        (nextStep, nextAction = null) => {
            const nextGameId = pageGameId;
            if (nextGameId === null) {
                return null;
            }
            return postJSON(
                route('display.game.update', { display: slug, game: nextGameId }),
                nextAction !== null ? { step: nextStep, action: nextAction } : { step: nextStep },
                {
                    credentials: 'include',
                    headers: getCSRFHeaders(),
                },
            );
        },
        [slug, pageGameId],
    );

    const changeStep = useCallback(
        (newStep = null, gameId = null) => {
            const url = route('display.step', { display: slug, step: newStep });
            // console.log('newStep', newStep, 'gameId', gameId);

            if (newStep !== null && gameId !== null && ['start', 'games'].indexOf(newStep) === -1) {
                updateStep(newStep);
            }

            if (gameId !== null && pageGameId !== gameId) {
                updateCurrentGame(gameId);
            }

            navigate(url);
        },
        [slug, updateStep, updateCurrentGame, navigate],
    );

    const notify = useCallback(
        (notification = null) => {
            if (pageGameId === null || notification === null) {
                return;
            }
            postJSON(
                route('display.game.notify', { display: slug, game: pageGameId }),
                { notification },
                {
                    credentials: 'include',
                    headers: getCSRFHeaders(),
                },
            );
        },
        [slug, pageGameId],
    );

    const home = route('display', { display: slug || 'live-quiz' });
    const { title: gameTitle = null, subtitle: gameSubtitle = null } = game || {};

    return (
        <div
            className={classNames([
                styles.container,
                themeClassName,
                {
                    [styles.vertical]: vertical,
                },
            ])}
        >
            <header className={styles.header}>
                {theme === 'defis' ? (
                    <img className={styles.leftImage} src={Book} alt="leftImage" />
                ) : null}
                <div className={styles.logoContainer}>
                    <Logo
                        component={logoComponent}
                        className={styles.logo}
                        href={home}
                        onClick={null}
                    />
                    {gameTitle !== null ? (
                        <p className={styles.title}>
                            {gameTitle} - {gameSubtitle}
                        </p>
                    ) : (
                        <p className={styles.title}>
                            <FormattedMessage
                                defaultMessage="No game selected"
                                description="Display label"
                            />
                        </p>
                    )}
                </div>
                <StyledButton className={styles.button} href={home}>
                    <Home />
                </StyledButton>
            </header>
            <div className={styles.center}>
                <aside className={styles.sidebar}>
                    <Sidebar page={display} game={game} changeStep={changeStep} />
                </aside>
                <main className={styles.content}>
                    <Step
                        component={component}
                        page={display}
                        game={game}
                        updateStep={updateStep}
                        updateCurrentGame={updateCurrentGame}
                        resetCurrentGame={resetCurrentGame}
                        notify={notify}
                    >
                        {game !== null ? (
                            <StepPreview
                                className={styles.preview}
                                step={step}
                                game={game}
                                leaderboard={leaderboard}
                            />
                        ) : null}
                    </Step>
                </main>
            </div>
            <footer className={styles.footer}>
                <Footer
                    page={display}
                    game={game}
                    changeStep={changeStep}
                    updateStep={updateStep}
                />
            </footer>
        </div>
    );
}

LiveQuizController.propTypes = propTypes;

export default LiveQuizController;
